/**
 * Instalar la app, como un instalador de verdad.
 *
 * Cuando la app se usa desde una pestaña del navegador se ofrece instalarla
 * —una vez al abrir, y respetando el "ahora no" (`install-reminder.ts`)—. Lo
 * que ofrece depende del aparato (`install-path.ts`):
 *
 *   - Donde el navegador lo permite (Chrome, Edge, Android), un boton que abre
 *     su propio cuadro de instalacion: UN toque. La hoja sigue hasta que
 *     termina: "Instalando..." y luego "Lista".
 *   - En iPhone, iPad, Samsung, Firefox y Mac, los pasos de ese aparato, con los
 *     iconos que la persona va a buscar en pantalla.
 *   - Dentro de Facebook, Instagram o TikTok, como abrirla en el navegador.
 *
 * El mismo cuadro se abre a mano desde Configuracion (`openInstaller`).
 *
 * VA MONTADO EN `App`, NO EN EL MARCO, a proposito: tambien sale en la
 * pantalla de acceso. En iPhone conviene instalarla ANTES de entrar, porque la
 * app instalada no comparte la sesion de Safari —son dos cajas aparte— y entrar
 * primero en Safari obliga a entrar dos veces.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AppWindow,
  BellRing,
  Check,
  Copy,
  Download,
  Ellipsis,
  EllipsisVertical,
  ExternalLink,
  Menu,
  MonitorSmartphone,
  Share,
  Smartphone,
  SquarePlus,
  ScanLine,
  type LucideIcon,
} from 'lucide-react';
import { useBranding } from '../lib/branding.js';
import { currentEnv, iosDeviceName, isStandalone, type DeviceEnv } from '../lib/device.js';
import {
  closeInstaller,
  isAlreadyOnDevice,
  launchInstall,
  setInstallerPresence,
  useInstallerRequested,
  useInstallOffer,
  useJustInstalled,
} from '../lib/install.js';
import {
  delayBeforeOffer,
  installPath,
  isMobileBrowser,
  offersItself,
  openInChromeUrl,
  recognize,
  type Browser,
  type InstallPath,
  type Step,
  type StepIcon,
} from '../lib/install-path.js';
import {
  afterNotNow,
  readInstallReminder,
  saveInstallReminder,
  shouldOffer,
} from '../lib/install-reminder.js';
import { useTourActive } from '../lib/permissions-tour.js';
import { BrandLogo } from './BrandLogo.js';
import { Button, Modal } from './ui.js';

type Phase = 'offer' | 'installing' | 'done';

/**
 * Hay navegadores que no avisan al terminar de instalar (`appinstalled`). Si
 * la persona acepto y en seis segundos no llego el aviso, se da por instalada:
 * aceptada es instalada, y una hoja que se queda en "Instalando..." para
 * siempre se ve rota.
 */
const INSTALLING_GRACE_MS = 6_000;

export function Installer(): ReactNode {
  const branding = useBranding();
  const env = useMemo(currentEnv, []);
  const browser = useMemo(() => recognize(env), [env]);
  const [standalone] = useState(isStandalone);
  const offer = useInstallOffer();
  const justInstalled = useJustInstalled();
  const requested = useInstallerRequested();

  /*
   * Lo que se sabe al abrir: si toca ofrecerla segun el "ahora no", y si el
   * navegador ya la sabe instalada en el aparato. Hasta saberlo, nada: ofrecer
   * instalar algo que ya esta instalado es la forma rapida de que la persona
   * deje de leer este cuadro.
   */
  const [allowed, setAllowed] = useState(false);
  const [alreadyOnDevice, setAlreadyOnDevice] = useState(false);
  const [decided, setDecided] = useState(false);
  useEffect(() => {
    let alive = true;
    const due = shouldOffer(readInstallReminder(), Date.now());
    void isAlreadyOnDevice().then((on) => {
      if (!alive) return;
      setAlreadyOnDevice(on);
      setAllowed(due);
      setDecided(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const path = installPath(
    browser,
    { standalone: standalone || justInstalled, offer, alreadyOnDevice },
    branding.shortName,
  );

  const [auto, setAuto] = useState(false);
  const [phase, setPhase] = useState<Phase>('offer');
  const [busy, setBusy] = useState(false);

  const wants = allowed && offersItself(browser, path);
  const delay = delayBeforeOffer(browser, path);
  // Con el recorrido de permisos en curso no se ofrece: espera a que termine
  // (ver `lib/permissions-tour.ts`). Pasa cuando la oferta del navegador llega
  // tarde, con el recorrido ya abierto.
  const tourActive = useTourActive();
  useEffect(() => {
    if (!wants || auto || tourActive) return;
    const id = window.setTimeout(() => setAuto(true), delay);
    return () => window.clearTimeout(id);
  }, [wants, auto, tourActive, delay]);

  // "Instalando..." termina cuando el navegador avisa que quedo. Se deriva en
  // vez de sincronizarse con un efecto.
  const visiblePhase: Phase = phase === 'installing' && justInstalled ? 'done' : phase;

  const close = (): void => {
    // "Ahora no" solo cuenta si se ofrecio sola y no se instalo: abrirla a mano
    // y cerrarla no es decir que no.
    if (auto && visiblePhase === 'offer') {
      saveInstallReminder(afterNotNow(readInstallReminder(), Date.now()));
    }
    setAuto(false);
    setAllowed(false);
    setPhase('offer');
    closeInstaller();
  };

  const install = async (): Promise<void> => {
    setBusy(true);
    const outcome = await launchInstall();
    setBusy(false);
    if (outcome === 'accepted') {
      setPhase('installing');
      window.setTimeout(() => setPhase((p) => (p === 'installing' ? 'done' : p)), INSTALLING_GRACE_MS);
    } else if (outcome === 'dismissed') {
      close();
    }
  };

  const open = auto || requested;

  // Lo que espera el recorrido de permisos para no encimarse: decidiendo,
  // por ofrecerse o en pantalla.
  const busyForTour = !decided || wants || open;
  useEffect(() => {
    setInstallerPresence(busyForTour, open);
  }, [busyForTour, open]);
  useEffect(() => () => setInstallerPresence(false, false), []);

  if (!open) return null;

  const mobile = isMobileBrowser(browser);
  const finished = visiblePhase === 'done' || path.kind === 'installed';

  return (
    <Modal
      open
      onClose={close}
      title={finished ? 'Lista' : `Instala ${branding.shortName}`}
      size="sm"
      footer={
        <InstallerFooter
          path={path}
          phase={visiblePhase}
          finished={finished}
          busy={busy}
          onInstall={() => void install()}
          onClose={close}
        />
      }
    >
      {visiblePhase === 'installing' ? (
        <Status icon={<Spinning />} title="Instalando..." detail="Tu navegador la esta agregando. Tarda unos segundos." />
      ) : finished ? (
        <Status
          icon={<Done />}
          title={visiblePhase === 'done' ? 'Quedo instalada' : 'Ya esta instalada en este aparato'}
          detail={
            mobile
              ? 'Abrela desde su icono en tu pantalla de inicio: abre a pantalla completa, sin la barra del navegador.'
              : visiblePhase === 'done'
                ? 'Se abrio en su propia ventana. Ya puedes cerrar esta pestaña.'
                : 'Abrela desde tus aplicaciones: abre en su propia ventana.'
          }
        />
      ) : (
        <div className="space-y-5">
          <header className="flex items-center gap-4">
            <BrandLogo className="size-14 shrink-0" />
            <div className="min-w-0">
              <p className="text-[17px] font-semibold leading-tight truncate">{branding.name}</p>
              <p className="text-[13px] text-muted mt-0.5">Punto de venta, en este aparato</p>
            </div>
          </header>

          {path.kind === 'button' || path.kind === 'steps' ? <Benefits browser={browser} env={env} /> : null}

          {path.kind === 'steps' ? (
            <div>
              <p className="text-[14px] font-semibold">Asi se instala en {deviceWord(browser, env)}</p>
              <ol className="mt-2.5 space-y-2">
                {path.steps.map((step, i) => (
                  <StepRow key={i} n={i + 1} step={step} />
                ))}
              </ol>
              {path.note ? <p className="mt-3 text-[13px] text-muted text-pretty">{path.note}</p> : null}
            </div>
          ) : null}

          {path.kind === 'open-in' ? <OpenIn browser={path.browser} /> : null}

          {path.kind === 'impossible' ? (
            <p className="text-[15px] text-pretty">
              Este navegador no instala apps. Abrela en Chrome o Edge, o en el navegador de tu telefono.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function deviceWord(browser: Browser, env: DeviceEnv): string {
  if (browser.startsWith('ios-')) return `este ${iosDeviceName(env)}`;
  if (browser.startsWith('android-')) return 'este telefono';
  if (browser === 'mac-safari') return 'esta Mac';
  return 'esta computadora';
}

/** Por que instalarla, en tres renglones que se leen de un vistazo. */
function Benefits({ browser, env }: { browser: Browser; env: DeviceEnv }): ReactNode {
  const rows: { icon: LucideIcon; text: string }[] = [
    isMobileBrowser(browser)
      ? { icon: Smartphone, text: 'Con su icono y a pantalla completa' }
      : { icon: MonitorSmartphone, text: 'En su propia ventana, sin pestañas' },
    {
      icon: BellRing,
      // En iPhone y iPad es la razon de fondo: los avisos SOLO llegan con la app
      // instalada.
      text: browser.startsWith('ios-')
        ? `Avisos al telefono: en ${iosDeviceName(env)} solo llegan con la app instalada`
        : 'Avisos al telefono aunque la app este cerrada',
    },
    { icon: ScanLine, text: 'Abre directo en la caja, sin buscar la pestaña' },
  ];
  return (
    <ul className="space-y-2.5">
      {rows.map(({ icon: Icon, text }) => (
        <li key={text} className="flex items-center gap-3">
          <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary-soft text-primary">
            <Icon className="size-4" />
          </span>
          <span className="text-[14px] leading-snug font-medium">{text}</span>
        </li>
      ))}
    </ul>
  );
}

function InstallerFooter({
  path,
  phase,
  finished,
  busy,
  onInstall,
  onClose,
}: {
  path: InstallPath;
  phase: Phase;
  finished: boolean;
  busy: boolean;
  onInstall: () => void;
  onClose: () => void;
}): ReactNode {
  if (phase === 'installing') return null;
  if (finished) {
    return (
      <Button variant="primary" size="lg" block onClick={onClose}>
        Listo
      </Button>
    );
  }
  if (path.kind === 'button') {
    return (
      <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-2">
        <Button size="lg" onClick={onClose}>
          Ahora no
        </Button>
        <Button variant="primary" size="lg" block loading={busy} onClick={onInstall}>
          {busy ? null : <Download className="size-4" />}
          Instalar
        </Button>
      </div>
    );
  }
  if (path.kind === 'steps') {
    return (
      <Button variant="primary" size="lg" block onClick={onClose}>
        Entendido
      </Button>
    );
  }
  return (
    <div className="space-y-2">
      <CopyLink />
      <Button variant="ghost" block onClick={onClose}>
        Ahora no
      </Button>
    </div>
  );
}

const STEP_ICON: Record<StepIcon, LucideIcon> = {
  share: Share,
  add: SquarePlus,
  menu: Menu,
  'menu-vertical': EllipsisVertical,
  'menu-horizontal': Ellipsis,
  file: AppWindow,
  install: Download,
  done: Check,
};

function StepRow({ n, step }: { n: number; step: Step }): ReactNode {
  const Icon = STEP_ICON[step.icon];
  return (
    <li className="flex items-center gap-3 rounded-xl bg-surface-2 px-3 py-2.5">
      <span className="relative grid size-9 shrink-0 place-items-center rounded-lg border border-line bg-surface">
        <Icon className="size-4" />
        <span className="absolute -top-1.5 -left-1.5 grid size-[18px] place-items-center rounded-full bg-primary text-[10px] font-bold text-on-primary">
          {n}
        </span>
      </span>
      <span className="text-[14px] leading-snug text-pretty">
        {step.before}
        <strong className="font-semibold">{step.strong}</strong>
        {step.after}
      </span>
    </li>
  );
}

/** Dentro de la ventana de otra app no se instala: se abre en el navegador. */
function OpenIn({ browser }: { browser: 'Safari' | 'Chrome' }): ReactNode {
  return (
    <div className="space-y-4">
      <p className="text-[15px] text-pretty">
        Estas en la ventana de otra app y desde aqui no se instala. Abrela en{' '}
        <strong className="font-semibold">{browser}</strong> y ahi te aparece.
      </p>
      {browser === 'Safari' ? (
        <ol className="space-y-2">
          <StepRow n={1} step={{ icon: 'menu-horizontal', before: 'Toca el menu ', strong: '···', after: ' o el icono de la brujula' }} />
          <StepRow n={2} step={{ icon: 'share', before: 'Elige ', strong: 'Abrir en Safari' }} />
        </ol>
      ) : (
        <a
          href={openInChromeUrl(window.location.href)}
          className="flex h-13 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-on-primary"
        >
          <ExternalLink className="size-4" />
          Abrir en Chrome
        </a>
      )}
    </div>
  );
}

function CopyLink(): ReactNode {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_500);
    } catch {
      // Sin portapapeles —una vista web vieja— no hay nada que hacer aqui.
    }
  };
  return (
    <Button size="lg" block onClick={() => void copy()}>
      {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      {copied ? 'Enlace copiado' : 'Copiar el enlace'}
    </Button>
  );
}

function Status({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }): ReactNode {
  return (
    <div className="flex flex-col items-center px-2 py-2 text-center">
      {icon}
      <p className="mt-4 text-[17px] font-semibold">{title}</p>
      <p className="mt-1.5 max-w-[22rem] text-[14px] text-muted text-pretty">{detail}</p>
    </div>
  );
}

/** El sello dentro de un anillo que gira: se esta instalando. */
function Spinning(): ReactNode {
  return (
    <span className="relative grid size-20 place-items-center">
      <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-primary-soft border-t-primary motion-reduce:animate-none" />
      <BrandLogo className="size-12" />
    </span>
  );
}

function Done(): ReactNode {
  return (
    <span className="grid size-16 place-items-center rounded-full bg-primary text-on-primary animate-pop">
      <Check className="size-8" strokeWidth={3} />
    </span>
  );
}
